import { Injectable } from '@angular/core';

import { NgbCalendar, NgbDate } from '@ng-bootstrap/ng-bootstrap';

import { BuyerDashboardService } from '../dashboard/dashboard.service';
import { GeneralService } from 'app/_services/general.service';
import { NotificationService } from 'app/_services/notification.service';

import { ApiResponseModel } from 'app/_models/apiResponseModel';
import { Globals } from 'app/_models/globals';
import { StatusIdEnum, isAwaitingConfirmation, isFinalStatus } from 'app/_models/statusId';
import Swal from 'sweetalert2';
import { SweetAlertParams } from 'app/_models/notificationParams.model';
import { fileSizeToMB } from 'app/_utils/general-utils';
import { UserService } from 'app/_services/user.service';

declare const $: any;
declare const getJavascriptDateFromFormattedDate: any;
declare const getJavascriptDateFromIsoDate: any;
declare const isEmpty: any;
declare const numberWithTwoDecimals: any;
declare const formatDateToYYYYMMDD: any;
declare const setDateNextWorkingDay: any;
declare const getWorkDaysCount: any;

@Injectable({ providedIn: 'root' })
export class BuyerService
{
    private maxFileSizeInMB: number = 5;
    private readonly ALLOWED_EXTENSIONS = ['jpg', 'jpeg', 'gif', 'png', 'bmp'];
    public maxUploadImages: number = 10;

    constructor(
        private userService: UserService,
        private buyerDashboardService: BuyerDashboardService,
        private generalService: GeneralService,
        private notificationService: NotificationService,
        private globals: Globals)
    { }

    atualizarTotais(pedido: any) {
        pedido.total = this.obterTotalNumerico(pedido);
        pedido.total_com_frete = this.obterTotalNumericoComFrete(pedido);

        return pedido;
    }

    public disableCalendarOnWeekendsAndHolidays(date: NgbDate, calendar: NgbCalendar, feriados: string[]) {
        let data = formatDateToYYYYMMDD(new Date(date.year, date.month - 1, date.day), '-');
        let isHoliday = feriados.some(x => x === data);

        return calendar.getWeekday(date) >= 6 || isHoliday;
    }

    public enviarCotacaoCotador(callback: any) {
        const params: SweetAlertParams = {
            title: 'buyer.visao-geral.mensagens.pedir-ajuda.confirm',
            input: 'textarea',
            inputPlaceholder: 'buyer.visao-geral.mensagens.pedir-ajuda.motivo.placeholder',
            inputValidator: (value) => {
                return new Promise((resolve) => {
                    if (!isEmpty(value))
                        resolve('');
                    else
                        resolve(this.globals.translate('buyer.visao-geral.mensagens.pedir-ajuda.motivo.obrigatorio'));
                })
            },
            icon: 'warning',
            cancelButtonText: 'elemento.generico-botao.fechar',
            confirmButtonText: 'buyer.visao-geral.mensagens.pedir-ajuda.botao'
        }

        this.notificationService.showConfirm(params).then((result) => {
            if (!isEmpty(result.dismiss) && result.dismiss == Swal.DismissReason.cancel)
                return;

            let pediu_ajuda = !isEmpty(result.value);

            if (typeof callback === 'function')
                callback(pediu_ajuda, result.value);
        });
    }

    public enviarCotacaoCotadorExecutar(quotation_id: number, motivo: string, nAttempts: number, callbackSucesso, callbackFimLoader)
    {
        this.buyerDashboardService.backToSupport(quotation_id, motivo).subscribe({
            next: (response: ApiResponseModel<boolean>) => {
                if (response && response.success) {
                    if (typeof callbackSucesso === 'function')
                        callbackSucesso();

                    this.notificationService.showSuccessToastr(this.globals.translate('buyer.visao-geral.mensagens.pedir-ajuda.mensagem.sucesso.corpo'));
                }
                else
                    this.notificationService.showErrorToastr(this.globals.translate('buyer.visao-geral.mensagens.pedir-ajuda.mensagem.erro.corpo'));

                if (typeof callbackFimLoader === 'function')
                    callbackFimLoader();
            },
            error: error => {
                nAttempts = nAttempts || 1;
                console.log(error, nAttempts);

                if (nAttempts >= 5) {
                    if (typeof callbackFimLoader === 'function')
                        callbackFimLoader();

                    this.notificationService.showErrorToastr(this.globals.translate('buyer.visao-geral.mensagens.pedir-ajuda.mensagem.erro.corpo'));
                    return;
                }

                this.enviarCotacaoCotadorExecutar(quotation_id, motivo, ++nAttempts, callbackSucesso, callbackFimLoader);
            }
        });
    }

    public handleEditionInformationsLoad(agrupamento, deliveryTimes: any[], feriados: any[]) {
        agrupamento.pedidos.forEach(p => {
            const daysList = p.pecas.filter(p => p.delivery_day && p.delivery_day < 99)
                                    .map(p => (p.delivery_day));

            if(p.pecas.every(p => p.delivery_day == 99)) {
                this.handleEditItemsInfo(p, [], deliveryTimes, feriados);
            }

            this.generalService.getAllDeliveryDatesByWorkingDays(daysList).subscribe({
                next: (response: ApiResponseModel<{deliveryDates: { day: number, deliveryDate: Date }[]}>) => {
                    this.handleEditItemsInfo(p, response.result.deliveryDates, deliveryTimes, feriados);
                },
                error: error => {
                    console.log(error);
                }
            });
        });
    }

    handleEditItemsInfo(pedido: any, deliveryDates: any, deliveryTimes: any[], feriados: any[]) {
        pedido.pecas.forEach(item => {
            if(isFinalStatus(item.status) || !this.userService.userIsCentral()) {
                item.lockDate = true;
                item.lockQuantity = true;
                item.lockPrice = true;
                item.lockDiscount = true;
                return;
            }

            item.editIsBO = item.delivery_day === 99;
            item.editIsPF = false;
            item.showPF = false;
            item.lockPF = false;

            item.lockQuantity = isFinalStatus(item.status) || ![StatusIdEnum.Confirmado].includes(item.status);
            item.lockDate = isFinalStatus(item.status) || isAwaitingConfirmation(item.status);
            item.lockPrice =  isFinalStatus(item.status) || ![StatusIdEnum.Pedido, StatusIdEnum.EmAnalise, StatusIdEnum.Confirmado, StatusIdEnum.AguardandoTroca, StatusIdEnum.AguardandoDevolucao].includes(item.status);
            item.lockDiscount =  isFinalStatus(item.status) || ![StatusIdEnum.Pedido, StatusIdEnum.EmAnalise, StatusIdEnum.Confirmado, StatusIdEnum.AguardandoTroca, StatusIdEnum.AguardandoDevolucao].includes(item.status);

            if (!item.editIsBO) {
                let isPF = item.delivery_day <= 1 ? false : item.delivery_type == "PF";
                let dtsSelecionado = deliveryTimes.filter(dt => dt.delivery_time_day === item.delivery_day && dt.is_PF == isPF)[0];

                item.lockPF = dtsSelecionado.delivery_time_day > 4;
                item.showPF = dtsSelecionado.delivery_time_day > 1;
                item.editIsPF = dtsSelecionado.is_PF;

                if (!isFinalStatus(item.status)) {
                    let dataEntrega;
                    switch (item.status) {
                        case StatusIdEnum.AguardandoDevolucao:
                            dataEntrega = getJavascriptDateFromIsoDate(item.expected_pickup_date);
                            break;
                        case StatusIdEnum.AguardandoTroca:
                            dataEntrega = new Date(item.expected_replacement_date);
                            break;
                        default:
                            const dataEntregaString = deliveryDates.find(d => d.day == item.delivery_day)?.deliveryDate;
                            dataEntrega = dataEntregaString ? new Date(dataEntregaString) : new Date();
                            break;
                    }

                    if (item.status == StatusIdEnum.Confirmado || item.status == StatusIdEnum.AguardandoConfirmacaoTroca || item.status == StatusIdEnum.AguardandoConfirmacaoDevolucao) {
                        let manha = this.globals.translate('buyer.dados-pedido.itens.outros.manha');
                        let tarde = this.globals.translate('buyer.dados-pedido.itens.outros.tarde');
                        let horario = item.prazo_entrega.indexOf(manha) >= 0 ? ' 08:00' : ' 14:00';
                        dataEntrega = getJavascriptDateFromFormattedDate(item.prazo_entrega.replace(manha, "").replace(tarde, "") + horario);
                    }

                    item.data_entrega = { year: dataEntrega.getFullYear(), month: dataEntrega.getMonth() + 1, day: dataEntrega.getDate() };
                }
            } else {
                item.data_entrega = '';
            }

            this.handleEditDateChanged(item.data_entrega, item, feriados);
        });
    }

    handleEditDateChanged($event, peca, feriados) {
        if (typeof peca.data_entrega == 'string')
            return;

        let data_entrega = new Date($event.year, $event.month - 1, $event.day);
        data_entrega.setHours(data_entrega.getHours() + 3);

        let data_confirmacao = peca.status <= StatusIdEnum.EmAnalise ? new Date() : getJavascriptDateFromIsoDate(peca.confirmation_date);

        // Se a data de confirmação for após as 18h, coloca na manhã do próximo dia útil
        if (data_confirmacao.getHours() > 18)
            data_confirmacao = setDateNextWorkingDay(data_confirmacao, feriados);

        let working_days = getWorkDaysCount(data_confirmacao, data_entrega, feriados);

        peca.lockPF = working_days > 4 || peca.isPF;
        peca.showPF = working_days > 1;

        if(working_days > 4)
            peca.editIsPF = true;
    }

    public pedirAjudaPopularModal() {
        const params: SweetAlertParams = {
            title: 'buyer.visao-geral.mensagens.cta-v8.titulo',
            message: 'buyer.visao-geral.mensagens.cta-v8.corpo',
            confirmButtonText: 'buyer.visao-geral.mensagens.cta-v8.botao-migrar',
        }

        this.notificationService.showInfo(params).then((result) => {
            if (result.value) {
                window.open('https://cotexo.superlogica.net/clients/areadocliente/publico/onestepcheckout?id=1&url=https://app.partsfy.com.br/contratar-partsfy/&idplano=37');

                this.generalService.slack_enviar_growth().subscribe({ error: (e: any) => console.error(e) });
            }
        });
    }

    public obterPaginaAnteriorSemaforoVisaoGeral(previousPages: string[]) : string {
        let homePage = this.userService.user().homePage ?? '/comprador/visao-geral';

        // Se não tiver histórico ou somente a página atual no histórico, volta para a home
        if (isEmpty(previousPages) || previousPages.length <= 1)
            return homePage;

        let previousPage = previousPages[previousPages.length - 2];

        if (previousPage === '/alterar-senha' && previousPages.length >= 3)
            previousPage = previousPages[previousPages.length - 3];

        // Se a página anterior não for nem o curador, nem conversor e nem a visão geral, apaga esta e vai para a próxima
        if (previousPage.indexOf('curador') < 0 && previousPage.indexOf('conversor') < 0 && previousPage.indexOf('visao-geral') < 0) {
            previousPages.splice(0, 2);
            return this.obterPaginaAnteriorSemaforoVisaoGeral(previousPages);
        }

        // Caso tenha visão geral do comprador na url, volta para a visao geral principal
        if (previousPage.indexOf('/comprador/visao-geral') >= 0)
            previousPage = '/comprador/visao-geral';

        // Trata os demais casos de voltar para a visão geral
        if (previousPage.indexOf(homePage) >= 0)
            previousPage = homePage;

        // Caso seja, redireciona para a página anterior
        return previousPage;
    }

    public obterStatusClassName(status: number) {
        if (status == StatusIdEnum.Pedido)
            return 'yellow';
        else if (status == StatusIdEnum.EmAnalise)
            return 'warning';
        else if (status <= StatusIdEnum.AguardandoDevolucao)
            return 'success';
        else
            return 'danger';
    }

    public obterTotal(pedido: any) {
        const total = this.obterTotalNumerico(pedido);
        return total;
    }

    public obterTotalNumerico(pedido: any): number {
        if (isEmpty(pedido)) { return 0; }

        const pecas = pedido.pecas;
        let total = 0;
        const obterTotalPecaNumerico = this.obterTotalPecaNumerico;

        if (!isEmpty(pecas)) {
            $.each(pecas, function (i, peca) {
                let status = isEmpty(peca.status) ? peca.status_id : peca.status;

                if (status <= StatusIdEnum.AguardandoDevolucao || status == StatusIdEnum.Entregue) {
                    total += obterTotalPecaNumerico(peca);
                }
            });
        }

        return total;
    }

    public obterTotalNumericoComFrete(pedido: any): number {
        if (isEmpty(pedido)) { return 0; }

        const pecas = pedido.pecas;
        let total = 0;
        const obterTotalPecaNumerico = this.obterTotalPecaNumerico;

        if (!isEmpty(pecas)) {
            $.each(pecas, function (i, peca) {
                let status = isEmpty(peca.status) ? peca.status_id : peca.status;

                if (status <= StatusIdEnum.AguardandoDevolucao || status == StatusIdEnum.Entregue) {
                    total += obterTotalPecaNumerico(peca);
                }
            });
        }

        total += isEmpty(pedido.frete_valor) || pedido.frete_valor === 0 ? 0 : pedido.frete_valor;

        return total;
    }

    public obterTotalPeca(peca) {
        if (peca.valor_unitario == undefined || peca.desconto == undefined) {
            return '0,00';
        }

        // tslint:disable-next-line: prefer-const
        let total = this.obterTotalPecaNumerico(peca);
        return total;
    }

    public obterTotalPecaNumerico(peca) {
        if (peca == undefined || peca.valor_unitario == undefined || peca.desconto == undefined) {
            return 0;
        }

        return numberWithTwoDecimals(peca.quantidade * (peca.valor_unitario - (peca.valor_unitario * peca.desconto / 100)));
    }

    public async validateUploadImage(files: any, inputFiles: any) {
        const totalFiles = files.length + inputFiles.length;
        const totalFilesSelected = files.length;
        let isValid = true;

        if(totalFiles > this.maxUploadImages) {
            await this.notificationService.showWarningWithVariables('componente.image-picker.error.file-limit', { maxUploadImages: this.maxUploadImages.toString() });
            return false;
        }

        for (let i = 0; i < totalFilesSelected; i++) {
            let file = files[i];

            if (!file)
                continue;

            if (inputFiles.find(i => i.filename.toLowerCase() === file.name.toLowerCase())){
                await this.notificationService.showWarningWithVariables('componente.image-picker.error.file-name', { fileName: file.name });
                isValid = false;

                continue;
            }

            let extension = file.type;
            let ext = extension.replace(/(.*)\//g, '').toLowerCase();

            if (!this.ALLOWED_EXTENSIONS.includes(ext))  {
                await this.notificationService.showWarningWithVariables('componente.image-picker.error.file-type', { fileName: file.name });
                isValid = false;

                continue;
            }

            let fileSizeMB = fileSizeToMB(file);

            if (fileSizeMB > this.maxFileSizeInMB) {
                await this.notificationService.showWarningWithVariables('componente.image-picker.error.size-limit', { allowedMbs: this.maxFileSizeInMB.toString() });
                isValid = false;

                continue;
            }
        }

        return isValid;
    }
}
